import React from 'react';

export const timeOut = 250;

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * Format the phone number to be in the format (XXX) XXX-XXXX
 * @param value - The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

/**
 * Format the date of birth to be in the format MM/DD/YYYY
 * @param value - The date of birth to format
 * @returns The formatted date of birth
 */
export const formatDateOfBirth = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

/**
 * Capitalize the first letter of each word in a string
 * @param str - The string to capitalize
 * @returns The string with each word's first letter capitalized
 */
export const capitalizeWords = (str: string): string => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Creates a load more handler function for object-based state updates
 * @param setState - React state setter function
 * @param key - The key in the state object to update
 * @param increment - Number of items to add
 * @param maxLength - Maximum length to cap at
 * @returns A function that updates the state
 */
export const createLoadMoreHandler = <T extends Record<string, number>>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  key: keyof T,
  increment: number,
  maxLength: number,
): (() => void) => {
  return () => {
    setState((prev) => ({
      ...prev,
      [key]: Math.min((prev[key] as number) + increment, maxLength),
    }));
  };
};
